import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * The kinds of shift a timesheet entry can be.
 *
 * A small list a store keeps — "Regular", "Overtime", "Training" — with one
 * marked as the default so clocking in does not have to ask.
 *
 * All four calls answer the *whole list* back, so a change never needs a
 * second request to see its result: the response seeds the cache directly.
 */

const KEY = ['timesheet-types'];

/** `EmployeeTimeSheetTypeDto`. */
export interface TimesheetType {
  id: string;
  nameEn: string | null;
  nameEs: string | null;
  isDefault: boolean;
  createdAt: string | null;
  deletedAt: string | null;
}

/** `CreateTimeSheetTypeBindingModel`, and the update adds an `id`. */
export interface TimesheetTypeInput {
  nameEn: string;
  nameEs: string;
  isDefault: boolean;
}

type TypeList = { results: TimesheetType[] };

export function useTimesheetTypes() {
  return useQuery({
    queryKey: KEY,
    queryFn: () =>
      unwrap<TypeList>(api.get<ApiEnvelope<TypeList>>('timesheet/types')),
    select: (data) => data.results ?? [],
  });
}

/** Every mutation answers the new list, so it replaces the cached one. */
function useSeedList() {
  const queryClient = useQueryClient();
  return (data: TypeList) => queryClient.setQueryData(KEY, data);
}

export function useCreateTimesheetType() {
  const seed = useSeedList();

  return useMutation({
    mutationFn: (input: TimesheetTypeInput) =>
      unwrap<TypeList>(api.post('timesheet/types', input)),
    onSuccess: seed,
  });
}

export function useUpdateTimesheetType() {
  const seed = useSeedList();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TimesheetTypeInput }) =>
      unwrap<TypeList>(api.put('timesheet/types', { id, ...input })),
    onSuccess: seed,
  });
}

export function useDeleteTimesheetType() {
  const seed = useSeedList();

  return useMutation({
    mutationFn: (id: string) =>
      unwrap<TypeList>(api.delete(`timesheet/types/${id}`)),
    onSuccess: seed,
  });
}
